import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { useUser } from '@/hooks/useUser'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { BrandMark } from '@/components/ui/brand-mark'
import { useAuthLoadingWatchdog } from '@/auth/useAuthLoadingWatchdog'
import { clearLogtoSessionStorage } from '@/auth/recoverSession'

const AUTH_SETTLE_MS = 250

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

export function LoginPage() {
  const { signIn, isLoading, isAuthenticated } = useUser()
  const navigate = useNavigate()
  const [confirmedSignedIn, setConfirmedSignedIn] = useState(false)

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      // Intentional synchronous reset to cancel any stale "confirmed
      // signed-in" promotion when Logto flips back to loading/unauth mid-
      // rehydrate. See _authenticated.tsx for the symmetric pattern.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConfirmedSignedIn(false)
      return
    }
    const timer = setTimeout(() => setConfirmedSignedIn(true), AUTH_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [isLoading, isAuthenticated])

  useEffect(() => {
    if (confirmedSignedIn) {
      navigate({ to: '/dashboard' })
    }
  }, [confirmedSignedIn, navigate])

  // If the Logto SDK is wedged on isLoading=true, never block the user from
  // seeing the Sign-in button — they got here precisely *because* they want
  // to start a fresh sign-in. Clearing the cached session before the click
  // matches the recovery-card path and prevents the stuck rehydrate from
  // attaching itself to the fresh attempt.
  const stuck = useAuthLoadingWatchdog(isLoading && !isAuthenticated)
  const showSignInUi = !isLoading || stuck

  if (!showSignInUi) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      <div className="relative w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <BrandMark size="lg" />
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-lg">Sign in to DocFiller</CardTitle>
            <CardDescription>
              Continue with your work account to upload documents and let the
              agent extract and pre-fill fields.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                // If we got here because the SDK was wedged, wipe the cached
                // session so the fresh sign-in handshake starts clean — the
                // stuck rehydrate promise can't poison the new flow.
                if (stuck) clearLogtoSessionStorage()
                void signIn()
              }}
              disabled={isLoading && !stuck}
            >
              Sign in
              <ArrowRight />
            </Button>
          </CardContent>
          <CardFooter className="justify-center pt-0 text-xs text-muted-foreground">
            Single sign-on via Microsoft Entra.
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
